import { useContext, useEffect } from 'react';

import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import { assign } from 'helpers/window-utils';
import useMixPanel from 'hooks/use-mixpanel';
import { usePost } from 'hooks/use-post';
import { bool, func, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { URL_SUBSCRIPTION_PLAN_CHOICE } from 'utils/api-urls';
import { formatPercentage } from 'utils/old-ui-commons/formats';
import { PERSONAL_INFORMATION_URL, SETTINGS_BANK_ACCOUNT_URL } from 'utils/urls';

import { Link } from '@olist/design-system';
import { Box, Button, Text } from '@olist/united';

import { UnionIcon } from '../icons/union';
import {
  Commission,
  CustomBadgeValue,
  CustomPriceLabel,
  ModalLink,
  PlanSelector,
  PlanSelectorButtonTooltip,
  PlanSelectorContainer,
  ValueContainer,
} from './plans-choice-selector.style';
import { URL_REDIRECT_AFTER_SUCCESS } from './utils';

export const PLANS_NAME = {
  1: 'mensal',
  12: 'anual',
};

const PlansChoiceSelector = ({
  commission,
  description,
  discount,
  hasPersonalData,
  onOpenModal,
  periodicity,
  planBillingId,
  planId,
  planName,
  recurringPayment,
  selfChoose,
  signupCompleted,
}) => {
  const { t } = useTranslation();

  const { track } = useMixPanel();
  const { isLoading, isSuccess, submit } = usePost(URL_SUBSCRIPTION_PLAN_CHOICE);

  const { userInfo } = useContext(AuthContext);

  useEffect(() => {
    if (isSuccess) {
      sendGTMEvent('etapa_cadastro', {
        user_id: userInfo.email,
        etapa: 'escolha_plano',
      });
      track('Selected Plan in Checkout', { plan: `olist ${planName}` });
      assign(URL_REDIRECT_AFTER_SUCCESS());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, planName, track]);

  const showInterconn = () => {
    window?.OlistSupport?.sendMessage(
      t`Hello, I want to know more about olist store premium. What do I get with it?`
    );
    sendGTMEvent('escolha_plano', {
      user_id: userInfo.email,
    });
  };

  const handleSelectedPlan = async (canBeSelfChoose) => {
    if (canBeSelfChoose) {
      submit({ planId, planBillingId });
    } else {
      showInterconn();
    }
  };

  return (
    <PlanSelectorContainer flexDirection="column" className="plan-selector-item">
      <PlanSelector alignItems="flex-start" justifyContent="space-between" flexDirection="column">
        <div>
          <Text fontSize="26px" fontWeight="bold" color="#000000">
            {planName}
          </Text>
          <Text fontSize="14px" mb={20} color="#696D8C">
            {description}
          </Text>
          <ValueContainer>
            {selfChoose ? (
              <CustomPriceLabel>
                R$ <strong>{recurringPayment}</strong>/{t`month`}
                {discount && selfChoose && <CustomBadgeValue>{discount}</CustomBadgeValue>}
              </CustomPriceLabel>
            ) : (
              <div>
                <Box>
                  <UnionIcon />
                </Box>
                <Box mb="4px">
                  <h3>{t`Talk to our sales team`}</h3>
                </Box>
              </div>
            )}
          </ValueContainer>
          {selfChoose && (
            <Text fontSize="14px" color="#696D8C">
              {t`In the contract`} {PLANS_NAME[periodicity]}
            </Text>
          )}
          {selfChoose ? (
            <Commission mb={16} mt={21} fontWeight={600}>
              <span>
                {formatPercentage(commission)} {t`of commision`}{' '}
              </span>
              <ModalLink type="button" className="modal-link" onClick={onOpenModal}>
                {t`understand how it works`}
              </ModalLink>
            </Commission>
          ) : (
            <Commission mb={16} mt={21} fontWeight={600} fontSize="14px">
              <span>{t`Commission to be negotiated`}</span>
            </Commission>
          )}
        </div>
        <div>
          {!hasPersonalData || !signupCompleted ? (
            <PlanSelectorButtonTooltip
              content={
                <Link
                  href={!hasPersonalData ? PERSONAL_INFORMATION_URL : SETTINGS_BANK_ACCOUNT_URL}
                >
                  <Text.Link as="span" role="presentation" color="foundation.shadeWhite">
                    {t`complete registration and choose a plan`}
                  </Text.Link>
                </Link>
              }
              placement="bottom"
              trigger="hover"
              delay={200}
              width="225px"
            >
              <Button my={4} variation={selfChoose ? 'alternate' : 'default'} disabled>
                {!selfChoose ? t`request a proposal` : t`choose plan`}
              </Button>
            </PlanSelectorButtonTooltip>
          ) : (
            <Button
              name={planName.toLowerCase()}
              id="plan-btn"
              loading={isLoading}
              onClick={() => handleSelectedPlan(selfChoose)}
              my={4}
              variation={selfChoose ? 'alternate' : 'default'}
            >
              {!selfChoose ? t`request a proposal` : t`choose plan`}
            </Button>
          )}
        </div>
      </PlanSelector>
    </PlanSelectorContainer>
  );
};

PlansChoiceSelector.defaultProps = {
  discount: null,
};

PlansChoiceSelector.propTypes = {
  commission: number.isRequired,
  hasPersonalData: bool.isRequired,
  signupCompleted: bool.isRequired,
  planId: string.isRequired,
  planName: string.isRequired,
  recurringPayment: number.isRequired,
  planBillingId: string.isRequired,
  description: string.isRequired,
  discount: string,
  periodicity: number.isRequired,
  selfChoose: bool.isRequired,
  onOpenModal: func.isRequired,
};

export default PlansChoiceSelector;
